import { Checkbox, Popover } from '@mui/material';
import { forwardRef, useState } from 'react';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import DifferencesPopover from './DifferencesPopover';
import EastIcon from '@mui/icons-material/East';
import IssuesPopover from './IssuesPopover';
import WarningsPopover from './WarningsPopover';
import SimilarArticlesCell from './SimilarArticlesCell';
import { Tooltip } from 'react-tooltip';
import { SHEET_ARTICULOS } from '../constantsArticleExcel';

const getField = (obj = {}, field) => {
  let value = obj[field];
  if (value === null || value === undefined) value = obj[`${field}Og`];
  if (value === null || value === undefined) value = '';
  return value;
};

const getCellClass = (item, field) => {
  if (item?.type === 'edited' && item?.changedKeys?.includes(field)) return 'edited-cell';
  if (item?.type === 'with-issues' && item?.issues?.some((issue) => issue?.field === field)) return 'with-issues-cell';
  return '';
};

const ArticlesT2Row = forwardRef(({
  article,
  formats,
  isSelected,
  onToggle,
  isDisabled,
  selectedFormatKeys,
  onToggleFormat,
  onToggleAllFormats,
  markedDuplicateCode,
  onMarkDuplicate,
  'data-index': dataIndex,
}, ref) => {
  const [expanded, setExpanded] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const rowClass =
    article?.type === 'edited'
      ? 'edited'
      : article?.type === 'created'
      ? 'created'
      : article?.type === 'with-issues'
      ? 'with-issues'
      : '';

  return (
    <tbody ref={ref} data-index={dataIndex}>
      <tr className={`${rowClass} ${markedDuplicateCode ? 'marked-duplicated' : ''}`}>
        <td>
          <div>
            <Checkbox
              size="small"
              checked={isSelected ?? false}
              disabled={isDisabled || !!markedDuplicateCode}
              onChange={() => {
                onToggle?.();
                const selectableKeys = formats
                  .filter((f) => f.type !== 'with-out-changes' && f.type !== 'with-issues')
                  .map((f) => f._excelRow?.toString())
                  .filter(Boolean);
                const allKeys = formats.map((f) => f._excelRow?.toString()).filter(Boolean);
                onToggleAllFormats?.(isSelected ? allKeys : selectableKeys, !isSelected);
              }}
            />
          </div>
        </td>
        <td className={getCellClass(article, 'code')}>
          <div>
            {article?.type === 'with-issues' && (
              <IssuesPopover issues={article?.issues} excelRow={article?.excelRow} excelSheet={article?.excelSheet} />
            )}
            <WarningsPopover
              warnings={[
                ...(article?.warnings ?? []),
                ...formats.flatMap((f) =>
                  (f.warnings ?? []).map((w) => ({ ...w, message: `[Fila ${f._excelRow}] ${w.message}` })),
                ),
              ]}
              excelRow={article?.excelRow}
              excelSheet={SHEET_ARTICULOS}
            />
            {getField(article, 'code')}
          </div>
        </td>

        <td className={getCellClass(article, 'imglink')}>
          <div>
            {article.imgFile || getField(article, 'imglink') || article.imgBase64 ? (
              <img
                style={{ height: '50px', objectFit: 'contain', border: '1px solid #3c4145' }}
                src={
                  article.imgFile
                    ? URL.createObjectURL(article.imgFile)
                    : article.imgBase64 || getField(article, 'imglink')
                }
                alt="img"
              />
            ) : (
              <>
                <span
                  aria-owns={open ? 'mouse-over-t2-img' : undefined}
                  aria-haspopup="true"
                  onMouseEnter={(e) => setAnchorEl(e.currentTarget)}
                  onMouseLeave={() => setAnchorEl(null)}
                >
                  {getField(article, 'imglink') || <i>vacío</i>}
                </span>
                {getField(article, 'imglink') && (
                  <Popover
                    id="mouse-over-t2-img"
                    sx={{ pointerEvents: 'none' }}
                    open={open}
                    anchorEl={anchorEl}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                    onClose={() => setAnchorEl(null)}
                    disableRestoreFocus
                  >
                    <img src={getField(article, 'imglink')} alt="img" width="130rem" />
                  </Popover>
                )}
              </>
            )}
          </div>
        </td>

        <td className={getCellClass(article, 'linkEditable')}>
          <div>
            {getField(article, 'linkEditable') || <i>vacío</i>}
            <DifferencesPopover show={article?.type === 'edited' && article?.changedKeys?.includes('linkEditable')}>
              <div>
                <span>{article?.linkEditableOg || <i>vacío</i>}</span> <EastIcon /> <span>{article?.linkEditable}</span>
              </div>
            </DifferencesPopover>
          </div>
        </td>

        <td className={getCellClass(article, 'name')}>
          <div>
            <span
              className={`truncate-name tooltip-artefinal-name-${article.code}`}
              data-tooltip-id={`tooltip-artefinal-name-${article.code}`}
            >
              {getField(article, 'name') || <i>vacío</i>}
            </span>

            <Tooltip id={`tooltip-artefinal-name-${article.code}`} style={{ zIndex: 1000 }}>
              {getField(article, 'name')}
            </Tooltip>
            <DifferencesPopover show={article?.type === 'edited' && article?.changedKeys?.includes('name')}>
              <div>
                <span>{article?.nameOg}</span> <EastIcon /> <span>{article?.name}</span>
              </div>
            </DifferencesPopover>
          </div>
        </td>

        <SimilarArticlesCell
          similarArticles={article?.similarArticles}
          markedDuplicateCode={markedDuplicateCode}
          onMarkDuplicate={onMarkDuplicate}
          name={getField(article, 'name')}
        />

        <td className={getCellClass(article, 'hipermercado')}>
          <div>{getField(article, 'hipermercado') ? 'SI' : 'NO'}</div>
        </td>

        <td className={getCellClass(article, 'proximidad')}>
          <div>{getField(article, 'proximidad') ? 'SI' : 'NO'}</div>
        </td>

        <td className={getCellClass(article, 'ultraproximidad')}>
          <div>{getField(article, 'ultraproximidad') ? 'SI' : 'NO'}</div>
        </td>

        <td className={getCellClass(article, 'isMontaje')}>
          <div>{getField(article, 'isMontaje') ? 'SI' : 'NO'}</div>
        </td>

        <td className={getCellClass(article, 'active')}>
          <div>{getField(article, 'active') ? 'SI' : 'NO'}</div>
        </td>

        <td className={getCellClass(article, 'minQuantity')}>
          <div>{getField(article, 'minQuantity') || 1}</div>
        </td>

        <td className={getCellClass(article, 'tags')}>
          <div>
            {article?.tags?.join(', ')}
            <DifferencesPopover show={article?.type === 'edited' && article?.changedKeys?.includes('tags')}>
              <div>
                <span>{article?.tagsOg?.join(', ')}</span> <EastIcon /> <span>{article?.tags?.join(', ')}</span>
              </div>
            </DifferencesPopover>
          </div>
        </td>

        <td>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.3rem',
              cursor: formats.length > 0 ? 'pointer' : 'default',
            }}
            onClick={() => formats.length > 0 && setExpanded((e) => !e)}
          >
            <strong>
              {formats.length} formato{formats.length !== 1 ? 's' : ''}
            </strong>
            {formats.length > 0 &&
              (expanded ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />)}
          </div>
        </td>
        <td />
        <td />
      </tr>

      {expanded &&
        formats.map((fmt, i) => (
          <tr
            key={`${fmt.codeFormat ?? i}-${i}`}
            className={`t2-format-subrow ${fmt.type === 'created' ? 'created' : fmt.type === 'edited' ? 'edited' : ''}`}
          >
            <td style={{ borderRight: 'none', background: 'transparent' }}>
              <Checkbox
                size="small"
                checked={selectedFormatKeys?.has(fmt._excelRow?.toString()) ?? false}
                onChange={() => onToggleFormat?.(fmt._excelRow?.toString())}
                disabled={fmt.type === 'with-out-changes' || fmt.type === 'with-issues'}
              />
            </td>
            {Array.from({ length: 12 }).map((_, j) => (
              <td
                key={`${fmt.codeFormat ?? i}-empty-${j}`}
                style={{ borderRight: 'none', background: 'transparent' }}
              />
            ))}
            <td className={fmt.type === 'edited' && fmt.changedKeys?.includes('codeFormat') ? 'edited-cell' : ''}>
              <div>
                <WarningsPopover warnings={fmt?.warnings} excelRow={fmt?._excelRow} excelSheet={SHEET_ARTICULOS} />
                {fmt.codeFormat || <i>vacío</i>}
              </div>
            </td>
            <td className={fmt.type === 'edited' && fmt.changedKeys?.includes('format') ? 'edited-cell' : ''}>
              <div>{fmt.format || <i>vacío</i>}</div>
            </td>
            <td className={fmt.type === 'edited' && fmt.changedKeys?.includes('materialName') ? 'edited-cell' : ''}>
              <div>{fmt.materialName || <i>vacío</i>}</div>
            </td>
          </tr>
        ))}
    </tbody>
  );
});

export default ArticlesT2Row;
